<script setup lang="ts">
// MDS UI components import
import "@maersk-global/mds-components-core-tag";
import "@maersk-global/mds-components-core-table";
import "@maersk-global/mds-components-core-select";
import "@maersk-global/mds-components-core-option";
import "@maersk-global/mds-components-core-button";
import "@maersk-global/mds-components-core-pagination";
import "@maersk-global/mds-components-core-notification";

// Vue imports

import { computed, ref, watch, onMounted } from "vue";
import { replacePlaceholder } from "@/utils/string_formatter";
import ImportTableView from "@/components/import/ImportTableView.vue";
import { useImportAssetStore } from "@/store/import/import_asset_store";
import { useImportSearchIdsStore } from "@/store/import/import_id_store";
import { useSearchIdsStore } from "@/store/common/search_id_store";
import { useImportLocationStore } from "@/store/import/import_location_store";
import type { PaginationAttributes } from "@/interface/pagination_interface";
import ContainerPrintView from "@/components/common/ContainerPrintView.vue";
import ShimmerLoaderTableView from "@/components/common/ShimmerLoaderTableView.vue";
import { useSaveContainer } from "@/store/import/save_container_store";
import Loader from "@/components/Loader.vue";
import { ColumnName } from "@/interface/common/terminal_configuration_interface";

import { storeToRefs } from "pinia";
import { environmentData } from "@/helper/environment";

import { isAuthenticatedUser, storeDataInLS } from "@/utils/common";
import { useGlobalStore } from "@/store/global";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { ignLoginRedirect } from "@/utils/authentication-helpers";
import { appButtonClick } from "@/utils/analytics";

const globalStore = useGlobalStore();

// Use the store
const saveContainerStore = useSaveContainer();
const importAssetsStore = useImportAssetStore();
const searchStore = useImportSearchIdsStore();
const locationStore = useImportLocationStore();
const commonSearchStore = useSearchIdsStore();
const terminalConfigurationStore = useTerminalConfigurationStore();

// Reactive getters

const { filteredImportAsset, originalImportAsset, bookingNumbers, selectedContainer, totalContainersInWatchlist } =
  storeToRefs(importAssetsStore);

const { terminalSelected } = storeToRefs(locationStore);
const loader = computed((): boolean => importAssetsStore.isLoading);
const currentPage = ref<number>(1);
const searchInput = ref<string>("");
const isSaveContainerLoading = ref<boolean>(false);

const pagination = ref<PaginationAttributes>({
  totalPages: 0,
  page: 1,
  pageSize: 5,
  totalRecords: 0
});

const labels = {
  importResult: globalStore.sitecoreData.headings.import_availability_result,
  importLoader: globalStore.sitecoreData.common.import_loader,
  csv: globalStore.sitecoreData.action_button.export_csv,
  print: globalStore.sitecoreData.action_button.print,
  resetBillOfLadingFilter: globalStore.sitecoreData.action_button.bolFilter,
  searchIds: globalStore.sitecoreData.common.import_assets_search,
  search: globalStore.sitecoreData.common.search,
  noResult: globalStore.sitecoreData.search.no_search_result,
  filterBOL: globalStore.sitecoreData.search.filter_bol,
  saved: globalStore.sitecoreData.search.saved_container,
  savedContainer: globalStore.sitecoreData.success_code_messages.saveContainers,
  followupInDashboard: globalStore.sitecoreData.success_code_messages.followupInDashboard,
  alreadyPresent: globalStore.sitecoreData.success_code_messages.already_present,
  saveSuccess: globalStore.sitecoreData.success_code_messages.save_success,
  addInProgress: globalStore.sitecoreData.common.add_in_progress,
  watchlistButtonLabel: globalStore.sitecoreData.cta.watchlistBtn,
  appointmentGroupCodeInfo: "",
  holdProcedureDesc: "",
  holdProcedureLinkTitle: "",
  holdProcedureLinkUrl: "",
  viewDetailStartDesc: "",
  viewDetailLinkTitle: "",
  viewDetailLinkUrl: "",
  viewDetailEndDesc: "",
  thirdShiftAppointment: "",
  howFundsReleased: "",
  findOutText: "",
  findOutLinkText: "",
  findOutLinkUrl: "",
  paymentFeesDesc: "",
  loginPaycargoCTA: "",
  loginPaycargoLink: ""
};
onMounted((): void => {
  importAssetsStore.resetIntialState();
});

const getTerminalSpecificLabel = (fieldName: string): string => {
  const selectedOneStreamCode = locationStore.terminalSelected.oneStreamCode;
  if (globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.import) {
    return (
      globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.import[fieldName] ||
      globalStore.sitecoreData.table_columns.import[fieldName]
    );
  }
  return globalStore.sitecoreData.table_columns.import[fieldName];
};

// Container column name
const containerColumnNames = computed((): any => {
  const columns =
    terminalConfigurationStore.importConfiguration?.containerColumnNames?.length &&
    terminalConfigurationStore.importConfiguration.containerColumnNames
      .filter((item: ColumnName): boolean => item.enabled)
      .map((item: ColumnName): ColumnName => {
        const fieldName = `${item.id}`;
        const column = {
          ...item,
          label: getTerminalSpecificLabel(fieldName),
          width: "150px"
        };
        if (item.subDataKey != "" && item.subDataKey != null) {
          column.subDataLabel = getTerminalSpecificLabel(item.subDataKey);
        }
        return column;
      });
  return columns;
});

// Change of booking numbers
const changeBookingNumber = (e: Event): void => {
  appButtonClick("Import Availability", "Select BOL dropdown clicks", locationStore.terminalSelected.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  const target = e.target as HTMLSelectElement;
  const bookingNumber: string = typeof target.value === "string" ? target.value.trim() : target.value;
  if (bookingNumber !== "") {
    importAssetsStore.filterByBookingNumber(bookingNumber);
  } else {
    importAssetsStore.removeBookingNumberFilter();
  }
};

const getPageCount = computed((): number => {
  return pagination.value.totalPages;
});

// Page change
const paginationChange = (event: any): void => {
  appButtonClick("Import Availability", "Pagination Click", locationStore.terminalSelected.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  currentPage.value = event.detail;
};

const searchAssets = (): void => {
  importAssetsStore.searchFilterImportAsset(searchInput.value.trim().toLowerCase());
};

const validateUserDetail = async (): Promise<void> => {
  appButtonClick("Import Availability", "Save Containers", locationStore.terminalSelected.oneStreamCode, {
    selectedContainersToAdd: importAssetsStore.selectedContainer.length,
    ...locationStore.anayticsTerminalDetails
  });
  isSaveContainerLoading.value = true;
  const isUserAuthenticated = isAuthenticatedUser();
  if (isUserAuthenticated) {
    const addToWatchListPayload = {
      terminalId: locationStore.terminalSelected.id,
      containers: [],
      validate: false
    };
    importAssetsStore.selectedContainer.forEach((element: any) => {
      addToWatchListPayload.containers.push({
        containerId: element.containerNumber,
        billOfLading: element.billOfLadingNumber
      });
    });
    await saveContainerStore.saveContainer(addToWatchListPayload);
    isSaveContainerLoading.value = false;
    await importAssetsStore.fetchImportAssets();
  } else {
    storeDataInLS("containerNumbers", searchStore.importSearchIds, 10);
    storeDataInLS("selectedContainers", importAssetsStore.selectedContainer, 10);
    storeDataInLS("saveContainerTerminal", locationStore.terminalSelected, 10);
    localStorage.setItem("internal-redirect", JSON.stringify({ redirectUri: window.location.pathname }));
    ignLoginRedirect();
  }
};

const appnedDateInUrl = (url: string) => {
  if (url.includes(".pdf") || url.includes(".jpg") || url.includes(".png") || url.includes(".jpeg")) {
    return url + `?rev=${new Date().getTime()}`;
  }
  return url;
};

const doneSaved = (): void => {
  importAssetsStore.resetIntialState();
};

watch(
  (): any => importAssetsStore.pagination,
  (data: PaginationAttributes): void => {
    pagination.value = { ...data };
  },
  { immediate: true, deep: true }
);

const showAppointmentGroupCodeDesc = () => {
  if (locationStore.terminalSelected.oneStreamCode === "PE018U") {
    labels.appointmentGroupCodeInfo =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]
        ?.appointment_group_code || [];
  }
  return locationStore.terminalSelected.oneStreamCode === "PE018U";
};

const showHoldProcedureSection = () => {
  if (locationStore.terminalSelected.oneStreamCode === "US070U") {
    labels.holdProcedureDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.hold_procedure_desc ||
      [];
    labels.holdProcedureLinkTitle =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]
        ?.hold_procedure_link_title || [];
    labels.holdProcedureLinkUrl =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]
        ?.hold_procedure_link_url || [];
  }
  return locationStore.terminalSelected.oneStreamCode === "US070U";
};

const showAlertNotification = () => {
  if (globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message) {
    return (
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message &&
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message !==
        ""
    );
  }
  return false;
};

const showthirdShiftSection = () => {
  if (locationStore.terminalSelected.oneStreamCode === "US005U") {
    labels.thirdShiftAppointment =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .third_shift_appointment || [];
    labels.viewDetailStartDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_text_start || [];
    labels.viewDetailLinkTitle =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_link_text || [];
    labels.viewDetailLinkUrl =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_link_url || [];
    labels.viewDetailEndDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_text_end || [];
  }
  return locationStore.terminalSelected.oneStreamCode === "US005U";
};

const showViewDetailedSection = () => {
  if (locationStore.terminalSelected.oneStreamCode === "US107U") {
    labels.viewDetailStartDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]
        ?.view_detialed_text_start || [];
    labels.viewDetailLinkTitle =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]
        ?.view_detialed_link_text || [];
    labels.viewDetailLinkUrl =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]
        ?.view_detialed_link_url || [];
    labels.viewDetailEndDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]
        ?.view_detialed_text_end || [];
  }
  return locationStore.terminalSelected.oneStreamCode === "US107U";
};

const showMiamiFundsSection = () => {
  if (locationStore.terminalSelected.oneStreamCode === "US093U") {
    labels.viewDetailStartDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_text_start || [];
    labels.viewDetailLinkTitle =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_link_text || [];
    labels.viewDetailLinkUrl =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_link_url || [];
    labels.viewDetailEndDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .view_detialed_text_end || [];

    labels.howFundsReleased =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .how_funds_released || [];
    labels.findOutText =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import.find_out_text ||
      [];
    labels.findOutLinkText =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .find_out_link_text || [];
    labels.findOutLinkUrl =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .find_out_link_url || [];

    labels.paymentFeesDesc =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .payment_fees_desc || [];
    labels.loginPaycargoCTA =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .login_paycargo_cta || [];
    labels.loginPaycargoLink =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.import
        .login_paycargo_link || [];
  }
  return locationStore.terminalSelected.oneStreamCode === "US093U";
};
const downloadorOpenFile = (url: string) => {
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes(".pdf")) {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else if (lowerUrl.includes(".jpeg") || lowerUrl.includes(".jpg")) {
    window.open(url, "_blank"); // open image in new tab
  } else {
    window.open(url, "_blank"); // open other link in new tab
  }
};

const nagivateToWatchlist = () => {
  appButtonClick("Import Availability", "View Watchlist Button Click", locationStore.terminalSelected.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  const watchlistRoute = environmentData.watchlistRoute;
  const terminalId = locationStore.terminalSelected.id;
  const url = `${watchlistRoute}${terminalId}`;

  window.open(url, "_blank"); // Opens in a new tab
};
</script>

<template>
  <div v-if="showAlertNotification()" class="mrgn-btm-10">
    <mc-notification
      heading=""
      :body="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.message
      "
      :appearance="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.category
      "
    >
    </mc-notification>
  </div>
  <div class="main-content">
    <div v-if="isSaveContainerLoading">
      <Loader :isLoading="isSaveContainerLoading" :loadingMessage="labels.addInProgress" />
    </div>
    <div>
      <div v-if="loader || commonSearchStore.isScreenLoadingBack" class="search-card">
        <ShimmerLoaderTableView :msg="labels.importLoader" />
      </div>

      <div v-if="originalImportAsset.length != 0 && !loader">
        <div class="page-header">
          <h3>{{ labels.importResult }} ({{ filteredImportAsset.length }})</h3>
        </div>

        <div ref="importResultScrollTarget" class="search-card">
          <div v-if="showAppointmentGroupCodeDesc()" class="group_code_section">
            {{ labels.appointmentGroupCodeInfo }}
          </div>
          <div v-if="showHoldProcedureSection()" class="group_code_section">
            <span>{{ labels.holdProcedureDesc }}</span>
            <a :href="appnedDateInUrl(labels.holdProcedureLinkUrl)" target="_blank">{{
              labels.holdProcedureLinkTitle
            }}</a>
          </div>
          <div v-if="showViewDetailedSection()" class="group_code_section">
            <span>{{ labels.viewDetailStartDesc }}</span>
            <a :href="appnedDateInUrl(labels.viewDetailLinkUrl)" target="_blank">{{ labels.viewDetailLinkTitle }}</a>
            <span>{{ labels.viewDetailEndDesc }}</span>
          </div>
          <div v-if="showthirdShiftSection()" class="group_code_section">
            <div>{{ labels.thirdShiftAppointment }}</div>
            <div class="flex-5-gap">
              <span>{{ labels.viewDetailStartDesc }}</span>
              <a :href="appnedDateInUrl(labels.viewDetailLinkUrl)" target="_blank">{{ labels.viewDetailLinkTitle }}</a>
              <span>{{ labels.viewDetailEndDesc }}</span>
            </div>
          </div>
          <div v-if="showMiamiFundsSection()" class="group_code_section">
            <div class="flex-5-gap">
              <span>{{ labels.viewDetailStartDesc }}</span>
              <a :href="appnedDateInUrl(labels.viewDetailLinkUrl)" target="_blank">{{ labels.viewDetailLinkTitle }}</a>
              <span>{{ labels.viewDetailEndDesc }}</span>
            </div>
            <div class="flex-5-gap">
              <span
                ><b>{{ labels.howFundsReleased }}</b></span
              >
              <span>{{ labels.findOutText }}</span>
              <a :href="labels.findOutLinkUrl" target="_blank">{{ labels.findOutLinkText }}</a>
            </div>
          </div>

          <div class="devider"></div>
          <div class="container-action-buttons">
            <!-- Booking / BOL number drop down component-->
            <div class="container-action-buttons__search">
              <div v-if="bookingNumbers.length > 0">
                <mc-select
                  label=""
                  :placeholder="labels.filterBOL"
                  icon="file-magnifying-glass"
                  name="billOfLadingNumber"
                  clearbutton="true"
                  @optionselected="changeBookingNumber($event)"
                  @clearbuttonclick="changeBookingNumber($event)"
                >
                  <mc-option v-for="booking in bookingNumbers" :key="booking" :value="booking">
                    {{ booking }}
                  </mc-option>
                </mc-select>
              </div>
              <mc-input
                v-model="searchInput"
                style="width: 40%"
                icon="magnifying-glass"
                label=""
                :placeholder="labels.searchIds"
                name="username"
                @input="searchAssets"
              />
              <div style="margin-top: 4px">
                <mc-button
                  :disabled="selectedContainer.length === 0"
                  :loading="isSaveContainerLoading"
                  :label="labels.saved + (selectedContainer.length > 0 ? ` (${selectedContainer.length})` : '')"
                  variant="filled"
                  appearance="primary"
                  icon="container-stacked-alt"
                  @click="validateUserDetail"
                />
              </div>
            </div>
            <div class="container-action-buttons">
              <div style="margin-top: 4px">
                <mc-button
                  :label="
                    labels.watchlistButtonLabel +
                    (totalContainersInWatchlist > 0 ? ` (${totalContainersInWatchlist})` : '')
                  "
                  variant="filled"
                  appearance="primary"
                  icon="container-stacked-alt"
                  @click="nagivateToWatchlist"
                />
              </div>
              <ContainerPrintView
                :containers="filteredImportAsset"
                :fileName="'Import-Container'"
                :headerLabel="containerColumnNames"
                :is-disabled="!filteredImportAsset.length"
                :terminalOneStreamCode="locationStore.terminalSelected.oneStreamCode"
                :analyticsTerminalDetails="locationStore.anayticsTerminalDetails"
              />
            </div>

            <!-- ATA and ETA descriptions -->
          </div>

          <div class="button-top-margin-15px"></div>
          <div v-if="saveContainerStore.containerSavedSuccessful">
            <div v-if="saveContainerStore.alreadySavedContainer.length">
              <mc-notification
                heading=""
                :body="
                  replacePlaceholder(labels.alreadyPresent, {
                    alreadySavedContainersLength: saveContainerStore.alreadySavedContainer.length.toString()
                  })
                "
                appearance="warning"
                closable
                @close="doneSaved"
              >
                <div v-if="!saveContainerStore.savedContainers.length">
                  <a
                    :href="`${environmentData.watchlistRoute}${terminalSelected.id}`"
                    target="_blank"
                    class="mds-link--stand-alone margn_left_5"
                    >{{ labels.followupInDashboard }}</a
                  >
                </div>
              </mc-notification>
            </div>
            <div class="button-top-margin-15px"></div>
            <div v-if="saveContainerStore.savedContainers.length">
              <mc-notification
                :heading="labels.savedContainer"
                :body="
                  replacePlaceholder(labels.saveSuccess, {
                    savedContainersLength: saveContainerStore.savedContainers.length.toString()
                  })
                "
                appearance="success"
                closable
                @close="doneSaved"
              >
                <a
                  :href="`${environmentData.watchlistRoute}${terminalSelected.id}`"
                  target="_blank"
                  class="mds-link--stand-alone"
                  >{{ labels.followupInDashboard }}</a
                >
              </mc-notification>
            </div>
          </div>

          <!-- Import Table view for the import availability -->
          <div>
            <ImportTableView
              :assets="filteredImportAsset"
              :pagination="pagination"
              :currentPage="currentPage"
              :columnNames="containerColumnNames"
              :totalpages="getPageCount"
              :terminal="locationStore.terminalSelected.oneStreamCode"
              @pagechange="paginationChange"
            />
          </div>
          <div v-if="showMiamiFundsSection()" class="align_right">
            <div class="download_container_btn">
              <span class="button_desc">{{ labels.paymentFeesDesc }}</span>
              <div class="download_container_btn_padding">
                <mc-button
                  :label="labels.loginPaycargoCTA"
                  fit="large"
                  appearance="primary"
                  variant="outlined"
                  width="full-width"
                  @click="downloadorOpenFile(appnedDateInUrl(labels.loginPaycargoLink))"
                >
                </mc-button>
              </div>
            </div>
          </div>

          <!-- Pagination -->
        </div>
      </div>
    </div>
  </div>
</template>
